// @flow
import React, {useState, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import {DeleteOutline, Edit, InfoOutlined} from '@material-ui/icons';
import Collapsible from '../../../../elements/Collapsible/Collapsible';

import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../assets/styles/colors';
import DomainChangeModal from './DomainChangeModal';
import UpgradeCustomDomainModal from './UpgradeCustomDomainModal';
import CustomDomainModal from './CustomDomainModal';
import {Divider, IconButton, useMediaQuery} from '@material-ui/core';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import DisabledTierModal from './DisabledTierModal';
import Tag from '../../../../elements/Tag/Tag';
import {useIsFeatureInTier} from '../../../../utils/component';
import {useIsMinimalPlan} from '../../../store/utils';

import type {SaveGeneralSettingsRequest} from '../api';
import type {Domain} from '../../../../entities';
import PremiumFeatureBadge from '../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import {TIENDADA_LINKS} from '../../../../constants';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';

type Props = {
  // store: Store,
  isSaving?: boolean,
  domain?: string,
  saveGeneralSettings: ({dataGeneral: SaveGeneralSettingsRequest}) => void,
  validateDomain: ({subdomain: string}) => void,
  validationResult?: boolean,
  isValidating?: boolean,
  getDomainChangeToken: () => void,
  domainChangeToken?: string,
  isValidatingSsl?: boolean,
  sslIsValid?: boolean,
  isGeneratingSsl: boolean,
  startGeneratingSsl?: boolean,
  errorValidatingSsl?: string,
  errorGeneratingSsl?: string,
  validateSsl: ({domain: string}) => void,
  generateSsl: ({domain: string}) => void,
  customDomain?: Domain,
  getCustomDomain: ({storeId: string}) => void,
  storeId?: string,
  deleteCustomDomain: ({storeId: string}) => void,
  domainDeleted?: boolean,
};

const useStyles = makeStyles(theme => ({
  subtitle: {
    fontWeight: 600,
    display: 'flex',
    justifyContent: 'space-between',
  },
  domain: {
    color: colors.grey700,
    fontWeight: 500,
  },
  editDomainButton: {
    marginLeft: theme.spacing(2),
    backgroundColor: 'unset',
    color: colors.purple3,
    padding: '0 0 0 8px',
  },
  deleteDomainButton: {
    marginLeft: theme.spacing(2),
    backgroundColor: 'unset',
    color: colors.error,
    padding: '0 0 0 8px',
    alignSelf: 'flex-end',
  },
  text: {
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  textInfo: {
    color: colors.grey800,
    background: '#F4F4F6',
    padding: '2px 8px',
    borderRadius: 4,
    marginTop: 10,
    fontWeight: 600,
    fontSize: 12,
  },
  divider: {
    background: colors.grey200,
    marginBottom: theme.spacing(2),
  },
  newLabel: {
    background: '#F5F0FF',
    marginLeft: 10,
    fontWeight: 600,
    '& span': {
      color: '#7B39FF',
    },
  },
  textHelper: {
    color: colors.grey900,
    fontSize: '0.875rem',
    margin: 0,
  },
  textHelperLink: {
    color: colors.grey900,
    fontSize: '0.875rem',
    textDecoration: 'none',
    fontWeight: 600,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexDirection: 'column',
    gap: theme.spacing(1),

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  },
  tierDisabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
}));

export default function DomainConfiguration(props: Props) {
  const {
    // isSaving,
    // store,
    domain,
    saveGeneralSettings,
    validateDomain,
    validationResult,
    isValidating,
    getDomainChangeToken,
    domainChangeToken,

    isValidatingSsl,
    sslIsValid,
    isGeneratingSsl,
    errorGeneratingSsl,
    errorValidatingSsl,
    validateSsl,
    generateSsl,
    startGeneratingSsl,
    customDomain,
    getCustomDomain,
    storeId,
    deleteCustomDomain,
    domainDeleted,
  } = props;

  const classes = useStyles();
  const history = useHistory();
  const fromTablet = useMediaQuery('(min-width: 768px)');

  const [openDisabledTier, setOpenDisabledTier] = useState(false);
  const [domainChangeModalOpen, setDomainChangeModalOpen] = useState(false);
  const [upgradeToCustomDomain, setUpgradeToCustomDomain] = useState(false);
  const [customDomainModal, setCustomDomainModal] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [waitToGenerateSsl, setWaitToGenerateSsl] = useState(false);

  const customDomainIsActive = useIsFeatureInTier('CUSTOM_DOMAIN');

  const isMinimalPlan = useIsMinimalPlan();

  useEffect(() => {
    if (storeId) {
      getCustomDomain({storeId});
    }
  }, [storeId, getCustomDomain]);

  useEffect(() => {
    if (!customDomain) return;
    const domainIsActive = Boolean(customDomain.sslGenerated);
    setWaitToGenerateSsl(!domainIsActive);
  }, [customDomain]);

  useEffect(() => {
    if (!domainDeleted) return;

    const id = setTimeout(() => {
      history.go(0);
    }, 1000);

    return () => clearTimeout(id);
  }, [domainDeleted, history]);

  const handleCloseDomainChangeModal = success => {
    setCustomDomainModal(false);
    if (success) {
      setWaitToGenerateSsl(true);
    }
  };

  const handleDeleteCustomDomain = () => {
    if (!storeId) return;
    deleteCustomDomain({storeId});
  };

  return (
    <>
      <Collapsible title={<>Dominio</>}>
        <div className={classes.subtitle}>
          <span>Tu dominio {fromTablet && 'gratuito'}</span>
          <CustomButton
            variant={'text'}
            className={classes.editDomainButton}
            startIcon={<Edit />}
            onClick={() => setDomainChangeModalOpen(true)}
          >
            Editar
          </CustomButton>
        </div>
        <Typography className={classes.domain} paragraph>
          {domain}
        </Typography>

        <Typography
          className={classes.text}
          paragraph
          style={{maxWidth: fromTablet ? '75%' : '100%'}}
        >
          Te brindamos un subdominio de TiendaDa. Se customiza con el nombre de
          tu marca. Recuerda que para acceder a tu panel siempre usarás este
          subdominio/admin.
        </Typography>

        <Divider className={classes.divider} />

        <div className={classes.subtitle}>
          <span>
            Usa tu propio dominio{' '}
            {!customDomainIsActive && <PremiumFeatureBadge />}
            <IconButton
              size="small"
              onClick={() => {
                setOpenDisabledTier(true);
              }}
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </span>
          <div>
            {fromTablet && (
              <>
                {waitToGenerateSsl && <Tag label="En proceso" type="warning" />}
                {customDomain && !waitToGenerateSsl && (
                  <>
                    <Tag label="Activo" type="success" />
                    <CustomButton
                      variant={'text'}
                      className={classes.deleteDomainButton}
                      startIcon={<DeleteOutline />}
                      onClick={() => setConfirmModalOpen(true)}
                    >
                      Eliminar
                    </CustomButton>
                  </>
                )}
              </>
            )}

            <CustomButton
              variant={'text'}
              className={`${classes.editDomainButton} ${
                isMinimalPlan ? classes.tierDisabled : ''
              }`}
              startIcon={<Edit />}
              onClick={() => {
                if (customDomainIsActive) {
                  setCustomDomainModal(true);
                } else {
                  setUpgradeToCustomDomain(true);
                }
              }}
            >
              Editar
            </CustomButton>
          </div>
        </div>
        <Typography className={classes.text} paragraph>
          Si tienes tu propio dominio puedes configurarlo aquí. Y si no cuentas
          con uno, te recomendamos obtener uno de godaddy.com.
        </Typography>

        {!fromTablet && (
          <>
            {waitToGenerateSsl && <Tag label="En proceso" type="warning" />}
            {customDomain && !waitToGenerateSsl && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <Tag label="Activo" type="success" />
                <CustomButton
                  variant={'text'}
                  className={classes.deleteDomainButton}
                  startIcon={<DeleteOutline />}
                  onClick={() => setConfirmModalOpen(true)}
                >
                  Eliminar
                </CustomButton>
              </div>
            )}
          </>
        )}
        {waitToGenerateSsl && (
          <Typography className={classes.textInfo} paragraph>
            Una vez que esté configurado todos los pasos en el panel de tu
            proveedor de dominio, demora entre 2h a 24h poder visualizar el
            cambio.
          </Typography>
        )}
        <footer className={classes.footer}>
          <Typography className={classes.textHelper} paragraph>
            ¿Por qué puede fallar la configuración de tu dominio?{' '}
            <a
              className={classes.textHelperLink}
              target="_blank"
              rel="noopener noreferrer"
              href={TIENDADA_LINKS.tutorials.CUSTOM_DOMAIN}
            >
              Clic aquí.
            </a>
          </Typography>
          {waitToGenerateSsl && (
            <CustomButton
              variant={'text'}
              className={classes.deleteDomainButton}
              startIcon={<DeleteOutline />}
              onClick={() => {
                if (!storeId) return;
                deleteCustomDomain({storeId});
              }}
            >
              Eliminar
            </CustomButton>
          )}
        </footer>

        <DisabledTierModal
          open={openDisabledTier}
          handleClose={() => setOpenDisabledTier(false)}
        />
        <DomainChangeModal
          open={domainChangeModalOpen}
          handleClose={() => setDomainChangeModalOpen(false)}
          domain={domain}
          saveGeneralSettings={saveGeneralSettings}
          validateDomain={validateDomain}
          validationResult={validationResult}
          isValidating={isValidating}
          getDomainChangeToken={getDomainChangeToken}
          domainChangeToken={domainChangeToken}
        />
        <CustomDomainModal
          open={customDomainModal}
          handleClose={handleCloseDomainChangeModal}
          isValidatingSsl={isValidatingSsl}
          sslIsValid={sslIsValid}
          isGeneratingSsl={isGeneratingSsl}
          errorGeneratingSsl={errorGeneratingSsl}
          validateSsl={validateSsl}
          generateSsl={generateSsl}
          errorValidatingSsl={errorValidatingSsl}
          startGeneratingSsl={startGeneratingSsl}
          waitToGenerateSsl={waitToGenerateSsl}
          customDomain={customDomain}
        />
        <UpgradeCustomDomainModal
          open={upgradeToCustomDomain}
          handleClose={() => setUpgradeToCustomDomain(false)}
        />
        <SnackBar
          mode="success"
          message={domainDeleted ? 'Dominio eliminado' : ''}
        />
      </Collapsible>

      <ConfirmModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onCancel={() => setConfirmModalOpen(false)}
        onConfirm={() => {
          setConfirmModalOpen(false);
          handleDeleteCustomDomain();
        }}
        title="Eliminar dominio"
        message="¿Estás seguro que deseas eliminar este dominio?"
      />
    </>
  );
}

DomainConfiguration.propTypes = {
  // store: PropTypes.object,
  isSaving: PropTypes.bool,
  domain: PropTypes.string,
  saveGeneralSettings: PropTypes.func.isRequired,
  validateDomain: PropTypes.func.isRequired,
  validationResult: PropTypes.bool,
  isValidating: PropTypes.bool,
  getDomainChangeToken: PropTypes.func.isRequired,
  domainChangeToken: PropTypes.string,
  isValidatingSsl: PropTypes.bool,
  sslIsValid: PropTypes.bool,
  errorValidatingSsl: PropTypes.string,
  isGeneratingSsl: PropTypes.bool,
  startGeneratingSsl: PropTypes.bool,
  errorGeneratingSsl: PropTypes.string,
  validateSsl: PropTypes.func.isRequired,
  generateSsl: PropTypes.func.isRequired,
};
